import type { Product } from '~/types'
import { shopCurrencyLabel } from '~/utils/shopCurrency'
import { formatPrice } from '~/composables/useSeo'
import { normalizePriceUnit } from '~/utils/shopServices'
import { attributeFromPriceDelta, productUsesAttributePricing } from '~/utils/productVariants'

export interface ProductCardAttribute {
  name: string
  value: string
}

export interface ProductCardPricing {
  displayPrice: number
  originalPrice: number
  hasDiscount: boolean
  discountPercent: number
  savingsAmount: number
}

function toPrice(value?: number | string | null): number | null {
  if (value === undefined || value === null || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export function getProductCardPricing(
  product: Pick<Product, 'price' | 'salePrice' | 'variationAttributes'>,
): ProductCardPricing {
  const listPrice = toPrice(product.price) ?? 0
  const salePrice = toPrice(product.salePrice)
  const hasDiscount = salePrice !== null && salePrice > 0 && salePrice < listPrice
  const extra = productUsesAttributePricing(product.variationAttributes)
    ? attributeFromPriceDelta(product.variationAttributes)
    : 0
  const displayPrice = (hasDiscount ? salePrice : listPrice) + extra
  const originalPrice = listPrice + extra
  const discountPercent = hasDiscount && listPrice > 0
    ? Math.round((1 - salePrice / listPrice) * 100)
    : 0
  const savingsAmount = hasDiscount ? listPrice - salePrice : 0

  return {
    displayPrice,
    originalPrice,
    hasDiscount,
    discountPercent,
    savingsAmount,
  }
}

export function getProductCardAttributes(product: Product): ProductCardAttribute[] {
  const assignments = product.attributeAssignments

  if (assignments?.length) {
    return [...assignments]
      .filter((item) => item.attribute?.showOnCard !== false)
      .sort((a, b) => (a.attribute?.sortOrder ?? 0) - (b.attribute?.sortOrder ?? 0))
      .map((item) => ({
        name: item.attribute?.name ?? '',
        value: item.value?.name ?? '',
      }))
      .filter((item): item is ProductCardAttribute => Boolean(item.name && item.value))
  }

  const fallback: ProductCardAttribute[] = []
  if (product.color) fallback.push({ name: 'Culoare', value: product.color })
  if (product.storage) fallback.push({ name: 'Stocare', value: product.storage })
  return fallback
}

export function getProductCardSubtitle(product: Product): string | null {
  const attributes = getProductCardAttributes(product)
  if (!attributes.length) return null
  return attributes.map((item) => `${item.name}: ${item.value}`).join(', ')
}

export function formatFlipPrice(amount: number): { whole: string, fraction: string, currency: string } {
  const settings = useState<Record<string, string>>('site-settings', () => ({}))
  const formatted = new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)

  const [whole, fraction] = formatted.split(',')
  return { whole, fraction, currency: shopCurrencyLabel(settings.value.shop_currency) }
}

export function formatSavingsAmount(amount: number): string {
  return new Intl.NumberFormat('ro-RO', {
    maximumFractionDigits: 0,
  }).format(Math.round(amount))
}

export function formatPriceWithUnit(amount: number | string, unit?: string | null): string {
  const price = formatPrice(amount)
  const label = normalizePriceUnit(unit)
  return label ? `${price} / ${label}` : price
}
